import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiFileText, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiClock,
  FiChevronLeft,
  FiLoader,
  FiCheck,
  FiCalendar,
  FiInfo,
  FiList,
  FiCheckSquare,
  FiX,
  FiEye,
  FiChevronDown,
  FiChevronUp
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { getTenantDocuments, acceptTenantDocument, acceptAllTenantDocuments } from '../../api';
import TenantAdminLayout from '../Layout/TenantAdminLayout';
import api from '../../api';

const TenantLegalDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [stats, setStats] = useState({ total: 0, accepted: 0, pending: 0 });

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: '',
    title: '',
    message: '',
    onConfirm: null,
    confirmText: '',
    cancelText: ''
  });

  // Success Modal
  const showSuccessModal = (title, message) => {
    setModal({
      isOpen: true,
      type: 'success',
      title,
      message,
      onConfirm: null,
      confirmText: 'OK',
      cancelText: ''
    });
  };

  // Error Modal
  const showErrorModal = (title, message) => {
    setModal({
      isOpen: true,
      type: 'error',
      title,
      message,
      onConfirm: null,
      confirmText: 'OK',
      cancelText: ''
    });
  };

  // Confirm Modal
  const showConfirmModal = (title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModal({
      isOpen: true,
      type: 'warning',
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModal((prevModal) => ({ ...prevModal, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modal.onConfirm) {
      modal.onConfirm();
    }
    closeModal();
  };

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTenantDocuments();
      setDocuments(response.data.documents);
      setStats({
        total: response.data.total,
        accepted: response.data.accepted,
        pending: response.data.pending
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
      showErrorModal('Error', 'Failed to fetch documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const fetchVersionHistory = async (documentType) => {
    setHistoryLoading(true);
    try {
      // Get all versions
      const response = await api.get(`/api/tenant-legal/history/${documentType}`);
      const versions = response.data.data.versions || [];
      
      // Fetch full content for each version
      const versionsWithContent = await Promise.all(
        versions.map(async (version) => {
          try {
            const contentResponse = await api.get(`/api/tenant-legal/version/${version._id}`);
            return {
              ...version,
              content: contentResponse.data.data.content || 'Content not available'
            };
          } catch (error) {
            console.error(`Error fetching content for version ${version.version}:`, error);
            return {
              ...version,
              content: 'Content not available'
            };
          }
        })
      );
      
      setVersionHistory(versionsWithContent);
      // Auto-expand the latest version
      if (versionsWithContent.length > 0) {
        setExpandedVersion(versionsWithContent[0]._id);
      }
    } catch (error) {
      console.error('Error fetching version history:', error);
      setVersionHistory([]);
      showErrorModal('Error', 'Failed to fetch version history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewHistory = async (doc) => {
    setSelectedDoc(doc);
    await fetchVersionHistory(doc.documentType);
    setShowVersionHistory(true);
  };

  const handleAccept = async (documentId) => {
    setAccepting(true);
    try {
      await acceptTenantDocument(documentId);
      await fetchDocuments();
      showSuccessModal('Success', 'Document accepted successfully!');
    } catch (error) {
      console.error('Error accepting document:', error);
      showErrorModal('Error', error.response?.data?.message || 'Failed to accept document');
    } finally {
      setAccepting(false);
    }
  };

  const handleAcceptAll = async () => {
    showConfirmModal(
      'Accept All Policies',
      `You are about to accept all ${stats.pending} pending policies. This action cannot be undone.`,
      async () => {
        setAcceptingAll(true);
        try {
          await acceptAllTenantDocuments();
          await fetchDocuments();
          showSuccessModal('Success', 'All documents accepted successfully!');
        } catch (error) {
          console.error('Error accepting all documents:', error);
          showErrorModal('Error', error.response?.data?.message || 'Failed to accept all documents');
        } finally {
          setAcceptingAll(false);
        }
      },
      'Accept All',
      'Cancel'
    );
  };

  const toggleVersionExpand = (versionId) => {
    setExpandedVersion(expandedVersion === versionId ? null : versionId);
  };

  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      terms_of_service: 'Terms of Service',
      privacy_policy: 'Privacy Policy',
      refund_policy: 'Refund Policy',
      cookie_policy: 'Cookie Policy'
    };
    return labels[type] || type;
  };

  const getDocumentIcon = (type) => {
    const icons = {
      terms_of_service: '📋',
      privacy_policy: '🔒',
      refund_policy: '💰',
      cookie_policy: '🍪'
    };
    return icons[type] || '📄';
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'published':
        return 'bg-green-100 text-green-700';
      case 'draft':
        return 'bg-yellow-100 text-yellow-700';
      case 'archived':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'published':
        return <FiCheckCircle className="w-3 h-3" />;
      case 'draft':
        return <FiClock className="w-3 h-3" />;
      case 'archived':
        return <FiAlertCircle className="w-3 h-3" />;
      default:
        return <FiInfo className="w-3 h-3" />;
    }
  };

  const ModalComponent = () => {
    const getModalStyles = () => {
      switch(modal.type) {
        case 'success':
          return {
            icon: <FiCheckCircle className="w-12 h-12 text-green-500" />,
            bg: 'bg-green-50',
            border: 'border-green-200',
            button: 'bg-green-600 hover:bg-green-700'
          };
        case 'error':
          return {
            icon: <FiAlertCircle className="w-12 h-12 text-red-500" />,
            bg: 'bg-red-50',
            border: 'border-red-200',
            button: 'bg-red-600 hover:bg-red-700'
          };
        case 'warning':
          return {
            icon: <FiAlertCircle className="w-12 h-12 text-amber-500" />,
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            button: 'bg-amber-600 hover:bg-amber-700'
          };
        default:
          return {
            icon: <FiInfo className="w-12 h-12 text-blue-500" />,
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            button: 'bg-blue-600 hover:bg-blue-700'
          };
      }
    };

    const styles = getModalStyles();

    return (
      <AnimatePresence>
        {modal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className={`bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ${styles.bg} border ${styles.border}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  {styles.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {modal.title}
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {modal.message}
                </p>
              </div>
              <div className={`px-6 py-4 ${styles.bg} border-t ${styles.border} flex flex-col sm:flex-row gap-3 justify-center`}>
                {modal.cancelText && (
                  <button
                    onClick={closeModal}
                    className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-colors text-sm font-medium border border-gray-200 w-full sm:w-auto"
                  >
                    {modal.cancelText}
                  </button>
                )}
                <button
                  onClick={handleModalConfirm}
                  className={`px-6 py-2.5 text-white rounded-xl transition-colors text-sm font-medium w-full sm:w-auto ${styles.button}`}
                >
                  {modal.confirmText || 'OK'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  if (loading) {
    return (
      <TenantAdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading documents...</p>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  return (
    <TenantAdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <button
              onClick={() => navigate('/tenant-admin/admin')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base"
            >
              <FiChevronLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Policies & Agreements</h1>
            <p className="text-sm text-gray-500 mt-0.5 sm:mt-1">
              Review and accept the latest policies and agreements
            </p>
          </div>
          {stats.pending > 0 && (
            <button
              onClick={handleAcceptAll}
              disabled={acceptingAll}
              className="px-3 sm:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm text-sm sm:text-base"
            >
              {acceptingAll ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <FiCheck className="w-4 h-4" />
                  Accept All ({stats.pending})
                </>
              )}
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 border border-gray-100">
            <p className="text-xs sm:text-sm text-gray-500">Total</p>
            <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 border border-gray-100">
            <p className="text-xs sm:text-sm text-gray-500">Accepted</p>
            <p className="text-lg sm:text-2xl font-bold text-green-600">{stats.accepted}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 border border-gray-100">
            <p className="text-xs sm:text-sm text-gray-500">Pending</p>
            <p className={`text-lg sm:text-2xl font-bold ${stats.pending > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {stats.pending}
            </p>
          </div>
        </div>

        {/* Documents List */}
        <div className="space-y-3 sm:space-y-4">
          {documents.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center border border-gray-100">
              <FiFileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No policies available</p>
            </div>
          ) : (
            documents.map((doc) => (
              <motion.div
                key={doc._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl shadow-sm p-4 sm:p-6 border transition-all ${
                  doc.accepted ? 'border-green-200' : doc.isDeadlinePassed ? 'border-red-200' : 'border-amber-200'
                }`}
              >
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="text-xl sm:text-2xl mt-0.5">{getDocumentIcon(doc.documentType)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{doc.title}</h3>
                        {doc.accepted ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                            <FiCheckCircle className="w-3 h-3" /> Accepted
                          </span>
                        ) : doc.isDeadlinePassed ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 flex items-center gap-1">
                            <FiAlertCircle className="w-3 h-3" /> Deadline Passed
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                            <FiClock className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </div>
                      
                      <div className="mt-1.5 flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="font-medium">v</span>{doc.version}
                        </span>
                        <span className="hidden xs:inline">•</span>
                        <span className="truncate">{getDocumentTypeLabel(doc.documentType)}</span>
                        {doc.effectiveDate && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <span className="flex items-center gap-1">
                              <FiCalendar className="w-3 h-3" />
                              {formatDate(doc.effectiveDate)}
                            </span>
                          </>
                        )}
                      </div>

                      {doc.changeLog && (
                        <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs sm:text-sm">
                          <p className="text-gray-600">
                            <span className="font-medium text-gray-700">What's new:</span> {doc.changeLog}
                          </p>
                        </div>
                      )}

                      {!doc.accepted && doc.content && (
                        <div className="mt-3">
                          <div 
                            className="text-xs sm:text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-200 max-h-[150px] overflow-y-auto prose prose-sm"
                            dangerouslySetInnerHTML={{ __html: doc.content }}
                          />
                        </div>
                      )}

                      {doc.accepted && doc.acceptedAt && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-green-600">
                          <FiCheckCircle className="w-3 h-3" />
                          Accepted on {formatDateTime(doc.acceptedAt)}
                        </div>
                      )}

                      {doc.isDeadlinePassed && !doc.accepted && (
                        <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
                          <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>⚠️ Acceptance deadline has passed. Must accept to continue.</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-2 border-t border-gray-100">
                    {!doc.accepted ? (
                      <button
                        onClick={() => handleAccept(doc._id)}
                        disabled={accepting}
                        className={`flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium shadow-sm ${
                          doc.isDeadlinePassed
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        }`}
                      >
                        {accepting ? (
                          <>
                            <FiLoader className="w-4 h-4 animate-spin" />
                            Accepting...
                          </>
                        ) : (
                          <>
                            <FiCheck className="w-4 h-4" />
                            Accept
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="flex-1 sm:flex-none text-green-600 flex items-center justify-center gap-1.5 text-sm font-medium">
                        <FiCheckCircle className="w-4 h-4" />
                        Accepted
                      </span>
                    )}
                    
                    <button
                      onClick={() => handleViewHistory(doc)}
                      className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <FiList className="w-4 h-4" />
                      <span className="hidden xs:inline">View History</span>
                      <span className="xs:hidden">History</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Version History Modal */}
      <AnimatePresence>
        {showVersionHistory && selectedDoc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowVersionHistory(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-gray-800 to-gray-900 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <FiList className="w-4 h-4 sm:w-5 sm:h-5 text-white flex-shrink-0" />
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-white truncate">Version History</h2>
                    <p className="text-xs text-gray-400 truncate">{selectedDoc.title}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowVersionHistory(false)}
                  className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors text-white/80 hover:text-white flex-shrink-0"
                >
                  <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-70px)] sm:max-h-[calc(85vh-80px)]">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
                    <p className="ml-3 text-gray-500 text-sm">Loading history...</p>
                  </div>
                ) : versionHistory.length > 0 ? (
                  <div className="space-y-3 sm:space-y-4">
                    {versionHistory.map((version, index) => {
                      const isExpanded = expandedVersion === version._id;
                      const isLatest = index === 0;
                      
                      return (
                        <motion.div
                          key={version._id || index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-3 sm:p-4 rounded-lg border-2 ${
                            version.status === 'published' 
                              ? 'bg-green-50 border-green-200' 
                              : version.status === 'draft'
                                ? 'bg-yellow-50 border-yellow-200'
                                : 'bg-gray-50 border-gray-200'
                          } ${isLatest ? 'ring-2 ring-indigo-400 ring-offset-2' : ''}`}
                        >
                          {/* Version Header */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-gray-900">v{version.version}</span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(version.status)} flex items-center gap-1`}>
                                {getStatusIcon(version.status)}
                                {version.status.charAt(0).toUpperCase() + version.status.slice(1)}
                              </span>
                              {isLatest && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-200 text-indigo-800">
                                  Latest
                                </span>
                              )}
                              {version.accepted && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-200 text-green-700 flex items-center gap-1">
                                  <FiCheckSquare className="w-3 h-3" /> Accepted
                                </span>
                              )}
                              {version.isDeadlinePassed && !version.accepted && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-200 text-red-700 flex items-center gap-1">
                                  <FiAlertCircle className="w-3 h-3" /> Deadline Passed
                                </span>
                              )}
                            </div>
                            {version.effectiveDate && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <FiCalendar className="w-3 h-3" />
                                {formatDate(version.effectiveDate)}
                              </span>
                            )}
                          </div>

                          {/* Change Log */}
                          {version.changeLog && (
                            <p className="mt-2 text-xs sm:text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-200">
                              <span className="font-medium">Change Log:</span> {version.changeLog}
                            </p>
                          )}

                          {/* Version Content - Expandable */}
                          <div className="mt-2">
                            <button
                              onClick={() => toggleVersionExpand(version._id)}
                              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              {isExpanded ? (
                                <>
                                  <FiChevronUp className="w-3 h-3" />
                                  Hide Content
                                </>
                              ) : (
                                <>
                                  <FiChevronDown className="w-3 h-3" />
                                  <FiEye className="w-3 h-3" />
                                  View Full Content
                                </>
                              )}
                            </button>
                            
                            {isExpanded && version.content && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-2"
                              >
                                <div 
                                  className="text-xs sm:text-sm text-gray-700 bg-white p-4 rounded-lg border border-gray-200 max-h-[400px] overflow-y-auto prose prose-sm"
                                  dangerouslySetInnerHTML={{ __html: version.content }}
                                />
                              </motion.div>
                            )}
                          </div>

                          {/* Meta Info */}
                          <div className="mt-2 flex flex-wrap gap-2 sm:gap-3 text-xs text-gray-400">
                            <span>Created: {formatDateTime(version.createdAt)}</span>
                            <span>Updated: {formatDateTime(version.updatedAt)}</span>
                            {version.acceptanceEndDate && (
                              <span className={version.isDeadlinePassed ? 'text-red-500' : 'text-amber-500'}>
                                Deadline: {formatDate(version.acceptanceEndDate)}
                                {version.daysRemaining !== null && version.daysRemaining > 0 && (
                                  <span className="text-gray-400 ml-1">({version.daysRemaining} days left)</span>
                                )}
                                {version.daysRemaining !== null && version.daysRemaining <= 0 && (
                                  <span className="text-red-500 ml-1">(Passed)</span>
                                )}
                              </span>
                            )}
                          </div>

                          {version.isDeadlinePassed && !version.accepted && (
                            <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                              <FiAlertCircle className="w-3 h-3" />
                              This version's acceptance deadline has passed
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <FiInfo className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No version history available</p>
                    <p className="text-sm text-gray-400 mt-1">This is the first version of this document</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Modal */}
      <ModalComponent />
    </TenantAdminLayout>
  );
};

export default TenantLegalDocuments;